/* eslint-disable @typescript-eslint/no-explicit-any */
// lib/ckeditorConfig.ts

import FinderJs from "./finderJS";

// CKEditor Type Definitions
export interface CKEditorConfig {
    toolbar?: any[];
    height?: number;
    width?: number | string;
    versionCheck?: boolean; // Thêm option để tắt version check
    filebrowserBrowseUrl?: string;
    filebrowserImageBrowseUrl?: string;
    filebrowserUploadUrl?: string;
    filebrowserImageUploadUrl?: string;
    filebrowserWindowWidth?: number;
    filebrowserWindowHeight?: number;
    removePlugins?: string;
    extraPlugins?: string;
    resize_enabled?: boolean;
    startupFocus?: boolean;
    readOnly?: boolean;
    balloonToolbars?: {
        text?: { buttons: string };
        image?: { buttons: string };
    };
    removeButtons?: string;
    [key: string]: any;
}

export interface FileManagerConfig {
    filebrowserBrowseUrl: string;
    filebrowserImageBrowseUrl: string;
    filebrowserUploadUrl: string;
    filebrowserImageUploadUrl: string;
    filebrowserWindowWidth: number;
    filebrowserWindowHeight: number;
}

export interface FileItem {
    name: string;
    size: number;
    date: Date;
    isDirectory: boolean;
    isImage: boolean;
    url: string;
}

export interface BrowseResponse {
    files: FileItem[];
    currentFolder: {
        path: string;
        url: string;
    };
}

export interface UploadResponse {
    uploaded: boolean;
    fileName: string;
    url: string;
    error?: string;
}

// Editor Types
export type EditorType = 'classic' | 'inline' | 'balloon' | 'simple' | 'minimal';

// Editor Data Interface
export interface EditorData {
    classic: string;
    inline: string;
    balloon: string;
    simple: string;
    minimal: string;
}

const removePlugins = 'uploadimage,elementspath,easyimage,cloudservices,exportpdf,image';
const extraPlugins = 'editorplaceholder,image2,resize';

/** Giữ thẻ <a> khi load HTML từ server; style link trong iframe qua contentsCss */
const CKEDITOR_LINK_ALLOWED_CONTENT = 'a[!href,target,rel,title]{*}(*); p br';
const CKEDITOR_LINK_CONTENTS_CSS = '/ckeditor/contents-links.css';

const configDefault = {
    versionCheck: false,
    disableNativeSpellChecker: true,
    scayt_autoStartup: false,
    startupFocus: true,
    removePlugins: removePlugins,
    resize_enabled: true,
    extraPlugins: extraPlugins,
    extraAllowedContent: CKEDITOR_LINK_ALLOWED_CONTENT,
    contentsCss: [CKEDITOR_LINK_CONTENTS_CSS],
}

// CKEditor Configurations
export const CKEDITOR_CONFIGS: Record<EditorType, CKEditorConfig> = {
    classic: {
        ...configDefault,
        toolbar: [
            { name: 'document', items: ['Source', '-', 'Save', 'NewPage', 'Preview', 'Print', '-', 'Templates'] },
            { name: 'clipboard', items: ['Cut', 'Copy', 'Paste', 'PasteText', 'PasteFromWord', '-', 'Undo', 'Redo'] },
            { name: 'editing', items: ['Find', 'Replace', '-', 'SelectAll', '-', 'SpellChecker', 'Scayt'] },
            { name: 'forms', items: ['Form', 'Checkbox', 'Radio', 'TextField', 'Textarea', 'Select', 'Button', 'ImageButton', 'HiddenField'] },
            '/',
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike', 'Subscript', 'Superscript', '-', 'RemoveFormat'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', '-', 'Blockquote', 'CreateDiv', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock', '-', 'BidiLtr', 'BidiRtl'] },
            { name: 'links', items: ['Link', 'Unlink', 'Anchor'] },
            { name: 'insert', items: ['Image', 'Flash', 'Table', 'HorizontalRule', 'Smiley', 'SpecialChar', 'PageBreak', 'Iframe'] },
            '/',
            { name: 'styles', items: ['Styles', 'Format', 'Font', 'FontSize'] },
            { name: 'colors', items: ['TextColor', 'BGColor'] },
            { name: 'tools', items: ['Maximize', 'ShowBlocks'] },
            { name: 'about', items: ['About'] }
        ],
        height: 400
    },

    inline: {
        ...configDefault,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike', '-', 'RemoveFormat'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent'] },
            { name: 'links', items: ['Link', 'Unlink'] },
            { name: 'insert', items: ['Image'] },
            { name: 'colors', items: ['TextColor', 'BGColor'] },
            { name: 'tools', items: ['Maximize'] }
        ]
    },

    balloon: {
        ...configDefault,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic'] },
            { name: 'links', items: ['Link', 'Unlink'] },
            { name: 'insert', items: ['Image'] }
        ],
        balloonToolbars: {
            text: {
                buttons: 'Bold,Italic,Link'
            },
            image: {
                buttons: 'Image'
            }
        }
    },

    simple: {
        ...configDefault,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'links', items: ['Link', 'Unlink'] }
        ],
        height: 200
    },

    minimal: {
        ...configDefault,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic'] },
            { name: 'links', items: ['Link'] }
        ],
        height: 150,
        removeButtons: ''
    }
};

// File Manager Configuration
export const FILE_MANAGER_CONFIG: FileManagerConfig = {
    filebrowserBrowseUrl: FinderJs.configPath().browseUrl,
    filebrowserImageBrowseUrl: FinderJs.configPath().imageBrowseUrl,
    filebrowserUploadUrl: '',
    filebrowserImageUploadUrl: '',
    filebrowserWindowWidth: 900,
    filebrowserWindowHeight: 600,
};

// CKEditor Instance Type (extend as needed)
declare global {
    interface Window {
        CKEDITOR: any;
    }
}